// src/components/notifications/NotificationInbox.jsx
// Comprehensive notification inbox with real-time updates

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, 
  Trash2, 
  Search,
  Eye,
  Users,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { useNotifications, markNotificationAsRead, archiveNotification } from '@/hooks/useNotifications';
import { useAuthState } from '@/auth/AuthProvider';
import AccessRequestNotification from './AccessRequestNotification';

const NotificationInbox = ({ 
  isOpen = false,
  onClose,
  onApproveRequest,
  onDenyRequest,
  onViewRequest,
  className = ''
}) => {
  const { user } = useAuthState();
  const navigate = useNavigate();

  // Use the notifications hook for real-time data
  const {
    notifications,
    unreadCount,
    loading,
    error,
    markAllAsRead,
    clearAll,
    searchNotifications
  } = useNotifications({
    userId: user?.uid
  });

  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'access_requests', 'system'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNotifications, setSelectedNotifications] = useState(new Set());

  // Mark all visible unread notifications as read when the inbox is opened
  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => {
      markAllAsRead().catch(() => {});
    }, 1500);
    return () => clearTimeout(timer);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  // Filter notifications
  const getFilteredNotifications = () => {
    let filtered = notifications;

    // Apply search first
    if (searchQuery.trim()) {
      filtered = searchNotifications(searchQuery);
    }

    // Then apply type filter
    if (filter === 'unread') {
      filtered = filtered.filter(n => n.status === 'unread');
    } else if (filter === 'access_requests') {
      filtered = filtered.filter(n => n.type.startsWith('access.'));
    } else if (filter === 'system') {
      filtered = filtered.filter(n => !n.type.startsWith('access.'));
    }

    return filtered;
  };

  const filteredNotifications = getFilteredNotifications();

  // Group notifications by date
  const groupedNotifications = filteredNotifications.reduce((groups, notification) => {
    const date = new Date(notification.createdAt.toDate()).toDateString();
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(notification);
    return groups;
  }, {});

  // Get notification counts
  const getCounts = () => {
    const total = notifications.length;
    const unread = unreadCount;
    const accessRequests = notifications.filter(n => n.type.startsWith('access.')).length;
    const system = notifications.filter(n => !n.type.startsWith('access.')).length;

    return { total, unread, accessRequests, system };
  };

  const counts = getCounts();

  // Handle notification selection
  const toggleNotificationSelection = (notificationId) => {
    const newSelected = new Set(selectedNotifications);
    if (newSelected.has(notificationId)) {
      newSelected.delete(notificationId);
    } else {
      newSelected.add(notificationId);
    }
    setSelectedNotifications(newSelected);
  };

  // Select all visible notifications
  const selectAllNotifications = () => {
    const allIds = filteredNotifications.map(n => n.id);
    setSelectedNotifications(new Set(allIds));
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedNotifications(new Set());
  };

  // Handle batch actions
  const handleBatchMarkAsRead = async () => {
    try {
      const promises = Array.from(selectedNotifications).map(id => markNotificationAsRead(id));
      await Promise.all(promises);
      clearSelection();
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  const handleBatchDelete = async () => {
    try {
      const promises = Array.from(selectedNotifications).map(id => archiveNotification(id));
      await Promise.all(promises);
      clearSelection();
    } catch (error) {
      console.error('Error deleting notifications:', error);
    }
  };

  // Get notification icon
  const getNotificationIcon = (type) => {
    if (type.startsWith('access.')) {
      if (type === 'access.requested') return Users;
      if (type === 'access.granted') return CheckCircle;
      if (type === 'access.denied') return XCircle;
    }
    
    return Bell;
  };

  // Format time ago
  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const diffMs = now - time;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return time.toLocaleDateString();
  };

  // Render notification item
  const renderNotification = (notification) => {
    const isSelected = selectedNotifications.has(notification.id);
    const isRead = notification.status !== 'unread';
    const Icon = getNotificationIcon(notification.type);

    // Special rendering for access request notifications
    if (notification.type === 'access.requested') {
      return (
        <div key={notification.id} className="relative">
          <div className="absolute left-4 top-4">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => toggleNotificationSelection(notification.id)}
              className="rounded border-border text-primary focus:ring-primary"
            />
          </div>
          
          <div className="pl-12">
            <AccessRequestNotification
              notification={notification}
              onApprove={onApproveRequest}
              onDeny={onDenyRequest}
              onView={onViewRequest}
              onMarkRead={() => markNotificationAsRead(notification.id)}
            />
          </div>
        </div>
      );
    }

    // Standard notification rendering
    const handleNotificationClick = async () => {
      if (!isRead) {
        await markNotificationAsRead(notification.id).catch((err) => {
          console.error('[NotificationInbox] Failed to mark notification as read on click:', err);
        });
      }
      if (notification.url) {
        onClose?.();
        if (notification.url.startsWith('/')) {
          navigate(notification.url);
        } else {
          window.open(notification.url, '_blank', 'noopener,noreferrer');
        }
      }
    };

    return (
      <div 
        key={notification.id}
        className={`flex items-start space-x-3 p-4 border border-slate-700/50 rounded-lg transition-all duration-200 hover:shadow-sm cursor-pointer ${
          isRead ? 'bg-slate-800/60' : 'bg-orange-500/10 border-orange-500/40'
        } ${isSelected ? 'ring-2 ring-orange-500' : ''}`}
        onClick={handleNotificationClick}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => {
            e.stopPropagation();
            toggleNotificationSelection(notification.id);
          }}
          className="rounded border-slate-600 text-orange-500 focus:ring-orange-500 mt-1"
        />

        <div className={`p-2 rounded-full ${isRead ? 'bg-slate-700/50' : 'bg-orange-500'}`}>
          <Icon className={`w-4 h-4 ${isRead ? 'text-slate-400' : 'text-white'}`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <h4 className={`font-medium truncate ${isRead ? 'text-slate-200' : 'text-orange-400'}`}>
              {notification.title}
            </h4>
            <div className="flex items-center space-x-2 ml-2">
              {!isRead && (
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
              )}
              <span className="text-xs text-slate-400 whitespace-nowrap">
                {formatTimeAgo(notification.createdAt)}
              </span>
            </div>
          </div>

          <p className="text-sm text-slate-400 line-clamp-2">
            {notification.body || notification.message}
          </p>

          {/* Notification actions */}
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center space-x-2">
              {notification.payload?.caseId && (
                <span className="text-xs text-slate-500">
                  Case #{notification.payload.caseId.slice(-6)}
                </span>
              )}
            </div>

            <div className="flex items-center space-x-1">
              {!isRead && (
                <button
                  data-testid={`notification.${notification.id}.markRead`}
                  onClick={(e) => {
                    e.stopPropagation();
                    markNotificationAsRead(notification.id).catch((err) => {
                      console.error('[NotificationInbox] Failed to mark notification as read:', err);
                    });
                  }}
                  className="p-1 rounded hover:bg-slate-700/50 text-slate-400 hover:text-slate-100"
                  title="Mark as read"
                >
                  <Eye className="w-3 h-3" />
                </button>
              )}
              
              <button
                data-testid={`notification.${notification.id}.delete`}
                onClick={(e) => {
                  e.stopPropagation();
                  archiveNotification(notification.id).catch((err) => {
                    console.error('[NotificationInbox] Failed to delete notification:', err);
                  });
                }}
                className="p-1 rounded hover:bg-slate-700/50 text-slate-400 hover:text-red-400"
                title="Delete"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Backdrop — click outside to close */}
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Dropdown panel */}
      <div className={`absolute right-0 top-full mt-2 z-50 w-[480px] max-w-[calc(100vw-2rem)] bg-slate-800 border border-slate-700/50 rounded-lg shadow-2xl ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-700/50">
        <div className="flex items-center space-x-3">
          <Bell className="w-5 h-5 text-orange-400" />
          <h2 className="text-lg font-semibold text-slate-100">Notifications</h2>
          {counts.unread > 0 && (
            <span className="px-2 py-1 bg-primary text-white text-xs rounded-full">
              {counts.unread}
            </span>
          )}
        </div>

        <div className="flex items-center space-x-2">
          {counts.unread > 0 && (
            <button
              data-testid="notifications.markAllRead"
              onClick={markAllAsRead}
              className="text-sm text-orange-400 hover:text-orange-300"
              disabled={loading}
            >
              Mark all read
            </button>
          )}
          
          {notifications.length > 0 && (
            <button
              data-testid="notifications.clearAll"
              onClick={clearAll}
              className="text-sm text-slate-400 hover:text-red-400"
              disabled={loading}
            >
              Clear all
            </button>
          )}

          {/* Close button */}
          {onClose && (
            <button
              data-testid="notifications.close"
              onClick={onClose}
              className="p-1 rounded hover:bg-slate-700/50 text-slate-400 hover:text-slate-100"
              aria-label="Close notifications"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Filters and search */}
      <div className="p-4 border-b border-slate-700/50">
        <div className="flex flex-col gap-2 mb-4">
          {/* Filter tabs */}
          <div className="flex items-center flex-wrap gap-1">
            {[
              { key: 'all', label: 'All', count: counts.total },
              { key: 'unread', label: 'Unread', count: counts.unread },
              { key: 'access_requests', label: 'Access Requests', count: counts.accessRequests },
              { key: 'system', label: 'System', count: counts.system }
            ].map(tab => (
              <button
                key={tab.key}
                data-testid={`notifications.filter.${tab.key}`}
                onClick={() => setFilter(tab.key)}
                className={`px-3 py-1.5 text-sm rounded transition-colors ${
                  filter === tab.key
                    ? 'bg-orange-500 text-white'
                    : 'text-slate-400 hover:text-slate-100 hover:bg-slate-700/50'
                }`}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span className={`ml-1 ${filter === tab.key ? 'text-white' : 'text-slate-500'}`}>
                    ({tab.count})
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              data-testid="notifications.search"
              placeholder="Search notifications..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-600/50 rounded-lg bg-slate-700/50 text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
        </div>

        {/* Batch actions */}
        {selectedNotifications.size > 0 && (
          <div className="flex items-center justify-between p-3 bg-orange-500/15 rounded-lg">
            <span className="text-sm text-orange-400 font-medium">
              {selectedNotifications.size} notification{selectedNotifications.size !== 1 ? 's' : ''} selected
            </span>
            
            <div className="flex items-center space-x-2">
              <button
                data-testid="notifications.batch.markRead"
                onClick={handleBatchMarkAsRead}
                className="px-3 py-1 bg-orange-500 text-white text-sm rounded hover:bg-orange-600"
              >
                Mark as read
              </button>
              <button
                data-testid="notifications.batch.delete"
                onClick={handleBatchDelete}
                className="px-3 py-1 bg-error text-white text-sm rounded hover:bg-error-hover"
              >
                Delete
              </button>
              <button
                data-testid="notifications.batch.clearSelection"
                onClick={clearSelection}
                className="px-3 py-1 text-slate-400 hover:text-slate-100 text-sm"
              >
                Clear selection
              </button>
            </div>
          </div>
        )}

        {/* Quick actions */}
        {filteredNotifications.length > 0 && selectedNotifications.size === 0 && (
          <div className="flex items-center space-x-4">
            <button
              data-testid="notifications.selectAll"
              onClick={selectAllNotifications}
              className="text-sm text-orange-400 hover:text-orange-300"
            >
              Select all
            </button>
            <span className="text-slate-500">•</span>
            <span className="text-sm text-slate-400">
              {filteredNotifications.length} notification{filteredNotifications.length !== 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>

      {/* Error state */}
      {error && (
        <div className="p-4 bg-red-950/40 border-l-4 border-red-500">
          <p className="text-red-400 text-sm">
            Error loading notifications: {error}
          </p>
        </div>
      )}

      {/* Loading state */}
      {loading && notifications.length === 0 && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
          <span className="ml-3 text-slate-400">Loading notifications...</span>
        </div>
      )}

      {/* Notification list */}
      <div className="max-h-96 overflow-y-auto">
        {Object.keys(groupedNotifications).length === 0 ? (
          <div className="text-center py-12">
            <Bell className="w-12 h-12 text-slate-500 mx-auto mb-4" />
            <p className="text-slate-400">
              {filter === 'unread' 
                ? 'No unread notifications'
                : filter === 'access_requests'
                ? 'No access request notifications'
                : filter === 'system'
                ? 'No system notifications'
                : 'No notifications'
              }
            </p>
          </div>
        ) : (
          <div className="p-4">
            {Object.entries(groupedNotifications)
              .sort(([a], [b]) => new Date(b).getTime() - new Date(a).getTime())
              .map(([date, notifications]) => (
                <div key={date} className="mb-6">
                  <h3 className="text-sm font-medium text-slate-400 mb-3 sticky top-0 bg-slate-800 py-2">
                    {new Date(date).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </h3>
                  
                  <div className="space-y-3">
                    {notifications
                      .sort((a, b) => new Date(b.createdAt.toDate()).getTime() - new Date(a.createdAt.toDate()).getTime())
                      .map(renderNotification)
                    }
                  </div>
                </div>
              ))
            }
          </div>
        )}
      </div>
      </div>
    </>
  );
};

export default NotificationInbox;